import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, PhoneCall, Stethoscope, Compass, Languages, ClipboardCheck, HeartPulse, AlertCircle, FileText, Clock, TrendingDown, Hospital, ShieldCheck } from 'lucide-react';
const capabilities = [{
  icon: PhoneCall,
  name: '24/7/365 Nurse Access',
  description: 'Round-the-clock access to registered nurses — every shift, every weekend, every holiday.'
}, {
  icon: Stethoscope,
  name: 'Immediate Clinical Assessment',
  description: 'Evidence-based triage protocols guide each conversation to the right level of care.'
}, {
  icon: Compass,
  name: 'Care Navigation',
  description: 'Direct injured workers to the most appropriate care setting — self-care, clinic, urgent care, or ER.'
}, {
  icon: ClipboardCheck,
  name: 'OSHA Recordability Guidance',
  description: 'Clinical input on first-aid versus recordable injury determinations, in real time.'
}, {
  icon: Languages,
  name: 'Multilingual Support',
  description: 'On-demand interpretation in 200+ languages so every worker gets understood.'
}, {
  icon: AlertCircle,
  name: 'Red Flag Identification',
  description: 'Trained protocols immediately escalate potentially serious or catastrophic injuries.'
}, {
  icon: HeartPulse,
  name: 'Self-Care Education',
  description: 'For minor injuries, nurses provide first-aid instructions and a follow-up plan.'
}, {
  icon: FileText,
  name: 'Documentation & Reporting',
  description: 'Every call documented and shared with employer, claims, and provider stakeholders.'
}];
const benefits = [{
  icon: Hospital,
  title: 'Fewer ER visits',
  stat: '40%',
  description: 'Average reduction in unnecessary emergency room utilization through proper care navigation.'
}, {
  icon: TrendingDown,
  title: 'Lower claim costs',
  stat: '$1.2K',
  description: 'Average savings per claim where nurse triage is engaged within the first hour of injury.'
}, {
  icon: Clock,
  title: 'Instant connection',
  stat: '< 30s',
  description: 'Average answer time. Injured workers reach a nurse — not a queue — when they need help.'
}, {
  icon: ShieldCheck,
  title: 'Compliance & accuracy',
  stat: '100%',
  description: 'OSHA-compliant documentation and recordability determinations on every call.'
}];
export function MedicalTriageNurse() {
  return <div className="bg-brand-paper" data-id="element-796">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-797">
        <div className="absolute inset-0 opacity-10" data-id="element-798">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-799"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-800"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-801">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-802">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-803">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-804">
                First Response Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-805">
              Medical <span className="text-brand-gold italic" data-id="element-806">Triage</span>{' '}
              Nurse
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-807">
              The first call after an injury should be to a nurse — not an
              emergency room. 24/7 telephonic triage that guides injured workers
              to the right level of care from the very first minute.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-808">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-809">
                Activate the Service <ArrowRight className="w-5 h-5 ml-2" data-id="element-810" />
              </Link>
              <a href="tel:8552601053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-811">
                <Phone className="w-5 h-5 mr-2" data-id="element-812" /> 855.260.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-white" data-id="element-813">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-814">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-815">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-816">
              Why It Matters
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-817">
              The first hour shapes the entire claim.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-818">
              When an injury happens, the default response is often "go to the
              ER." But research shows that the majority of workplace injuries
              can be safely managed with self-care or a clinic visit — saving
              thousands of dollars per claim and getting workers back to work
              faster.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-819">
              Our Medical Triage Nurse service puts an experienced RN on the
              phone immediately after an injury. We assess, advise, navigate,
              and document — turning the most chaotic moment of a claim into the
              most clinically-informed.
            </p>
            <ul className="space-y-4" data-id="element-820">
              {['24/7/365 access to RN-level clinical care', 'Evidence-based triage protocols', 'Reduces unnecessary ER and urgent care utilization', 'OSHA recordability support in real time'].map(item => <li key={item} className="flex items-start" data-id="element-821">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-822" />
                  <span className="text-gray-800 font-medium" data-id="element-823">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-824">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform rotate-2" data-id="element-825"></div>
            <img src="https://images.unsplash.com/photo-1612531386530-97286d97c2d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Nurse providing care and guidance over the phone" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-826" />
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-brand-paper" data-id="element-827">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-828">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-829">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-830">
              What's Included
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-831">
              Built for the moment injuries happen.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-832">
              Every triage call is supported by evidence-based protocols,
              multilingual access, and full documentation.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-833">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return <motion.div key={cap.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-834">
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-835">
                    <Icon className="w-6 h-6" data-id="element-836" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-837">
                    {cap.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-838">
                    {cap.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" data-id="element-839">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-840">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-841">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-842">
              Outcomes
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-843">
              The right care from minute one.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-844">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-845">
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-846"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-847" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-848">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-849">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-850">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-851">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-852"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-853">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-854">
            Put a nurse on the first call.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-855">
            Let's set up 24/7 nurse triage for your program. We'll show you how
            to launch and the savings you can expect from day one.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-856">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-857">
              Activate Triage
            </Link>
            <a href="tel:8552601053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-858">
              <Phone className="w-5 h-5 mr-2" data-id="element-859" /> 855.260.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}